import { Settings as SettingsIcon } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../components/ui/popover";
import { Switch } from "../../../components/ui/switch";
import { AutoVoteSection } from "./AutoVoteSection";
import { AutoRsvpSettingsCard } from "./AutoRsvpSettingsCard";
import { CacheControlsSection } from "./CacheControlsSection";
import { TimePicker } from "./TimePicker";

function durationLabel(h) {
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  if (hh === 0) return `${mm} min`;
  if (mm === 0) return `${hh} hr`;
  return `${hh} hr ${mm} min`;
}

function durationOptions() {
  const out = [];
  for (let i = 1; i < 96; i++) {
    const hours = i * 0.25;
    out.push({ value: hours, label: durationLabel(hours) });
  }
  return out;
}

const DURATION_OPTIONS = durationOptions();

/**
 * Gear-icon popover that consolidates all /availability page-level settings
 * in one place. Replaces the sidebar AutoVoteSection + sidebar
 * Show-hidden-events toggle + Settings page pointer card from Phase 2
 * chunk A4/A5. (See A11.)
 */
export function AvailabilitySettingsPopover({
  autoVoteMode = "off",
  onAutoVoteModeChange,
  autoVoteSaving = false,
  autoRsvpMode = "off",
  autoRsvpConflictTarget = "maybe",
  onAutoRsvpModeChange,
  onAutoRsvpConflictTargetChange,
  autoRsvpSaving = false,
  showHiddenValue,
  onShowHiddenChange,
  defaultDurationHours = 1,
  onDefaultDurationChange,
  defaultStartHour = null,
  onDefaultStartHourChange,
  prefSaving = false,
  cachedEventCount = 0,
  linkedCalendarCount = 0,
  onCacheDeleted,
  onCacheRefreshed,
}) {
  const useCurrentTime = defaultStartHour === null;
  const startTimeForPicker =
    typeof defaultStartHour === "string" ? defaultStartHour : "17:00";

  const handleStartModeChange = (mode) => {
    if (mode === "current") {
      onDefaultStartHourChange?.(null);
    } else {
      onDefaultStartHourChange?.("17:00");
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Availability settings"
          className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-xl border border-slate-300 bg-white text-slate-600 shadow-sm hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
        >
          <SettingsIcon className="h-4 w-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="max-h-[var(--radix-popover-content-available-height)] w-80 space-y-3 overflow-y-auto"
      >
        <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
          Availability settings
        </h3>
        <AutoVoteSection
          autoVoteMode={autoVoteMode}
          onAutoVoteModeChange={onAutoVoteModeChange}
          saving={autoVoteSaving}
        />
        <AutoRsvpSettingsCard
          autoRsvpMode={autoRsvpMode}
          autoRsvpConflictTarget={autoRsvpConflictTarget}
          onAutoRsvpModeChange={onAutoRsvpModeChange}
          onAutoRsvpConflictTargetChange={onAutoRsvpConflictTargetChange}
          saving={autoRsvpSaving}
        />
        <section className="space-y-2 border-t border-slate-200 pt-3 dark:border-slate-700">
          <div className="flex items-start justify-between gap-3 rounded-xl border border-slate-200 bg-white/60 px-3 py-2.5 dark:border-slate-700 dark:bg-slate-900/40">
            <div className="flex flex-col gap-1">
              <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
                Show hidden events
              </span>
              <span className="text-[11px] text-slate-500 dark:text-slate-400">
                When off, events you&apos;ve hidden (and sessions you&apos;ve
                RSVP&apos;d no to) are removed from this calendar entirely.
              </span>
            </div>
            <Switch
              checked={showHiddenValue}
              onCheckedChange={onShowHiddenChange}
              aria-label="Show hidden events"
            />
          </div>
        </section>
        <section className="space-y-3 border-t border-slate-200 pt-3 dark:border-slate-700">
          <div>
            <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              Click-to-create defaults
            </h4>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">
              Default duration applies whenever you click to add free time. Default
              start time is used in month view; in week and day views the
              clicked time is used as the start.
            </p>
          </div>
          <div className="flex items-center justify-between gap-3">
            <label
              htmlFor="default-mark-duration"
              className="text-sm font-medium text-slate-700 dark:text-slate-200"
            >
              Default duration
            </label>
            <select
              id="default-mark-duration"
              aria-label="Default duration"
              disabled={prefSaving}
              value={defaultDurationHours}
              onChange={(e) =>
                onDefaultDurationChange?.(Number(e.target.value))
              }
              className="inline-flex h-9 min-w-[8rem] items-center rounded-xl border border-slate-300 bg-white px-2 text-sm font-medium text-slate-800 shadow-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800"
            >
              {DURATION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
              Default start time (month view)
            </span>
            <div className="flex items-center gap-2">
              <input
                id="default-start-current"
                type="radio"
                name="default-start-mode"
                checked={useCurrentTime}
                onChange={() => handleStartModeChange("current")}
                disabled={prefSaving}
                className="h-4 w-4 text-brand-primary focus-visible:ring-2 focus-visible:ring-ring"
              />
              <label
                htmlFor="default-start-current"
                className="text-sm text-slate-700 dark:text-slate-200"
              >
                Use current time
              </label>
            </div>
            <div className="flex items-center gap-2">
              <input
                id="default-start-always"
                type="radio"
                name="default-start-mode"
                checked={!useCurrentTime}
                onChange={() => handleStartModeChange("always")}
                disabled={prefSaving}
                className="h-4 w-4 text-brand-primary focus-visible:ring-2 focus-visible:ring-ring"
              />
              <label
                htmlFor="default-start-always"
                className="text-sm text-slate-700 dark:text-slate-200"
              >
                Always at
              </label>
              <TimePicker
                kind="start"
                value={startTimeForPicker}
                onChange={(v) => onDefaultStartHourChange?.(v)}
                ariaLabel="Default start time"
                disabled={useCurrentTime || prefSaving}
              />
            </div>
          </div>
        </section>
        <CacheControlsSection
          eventCount={cachedEventCount}
          linkedCalendarCount={linkedCalendarCount}
          onDeleted={onCacheDeleted}
          onRefreshed={onCacheRefreshed}
        />
      </PopoverContent>
    </Popover>
  );
}
